import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FileText, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { TableSectionSkeleton } from './Skeletons';
import { formatDate } from '../utils/formatters';
import { TABLE_CONFIG } from '../utils/constants';


export default function AssessmentTable({ data, loading, currentPage = 0, totalCount = 0, onPageChange }) {
  const [expandedAssessment, setExpandedAssessment] = useState(null);

  if (loading) return <TableSectionSkeleton cols={TABLE_CONFIG.ASSESSMENT.cols} />;

  if (!data || data.length === 0) {
    return (
      <p className="px-6 py-8 text-center text-sm text-gray-400">
        No assessments attempted yet.
      </p>
    );
  }

  const PAGE_SIZE = 8;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handlePrevPage = () => {
    if (currentPage > 0) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      onPageChange(currentPage + 1);
    }
  };

  const startIndex = currentPage * PAGE_SIZE + 1;
  const endIndex = Math.min((currentPage + 1) * PAGE_SIZE, totalCount);

  const toggleAssessmentExpand = (assessmentId) => {
    setExpandedAssessment(expandedAssessment === assessmentId ? null : assessmentId);
  };

  return (
    <div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8" />
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Assessment
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Attempts
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Best Score
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Attempt
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {data.map((assessment) => {
            const isExpanded = expandedAssessment === assessment.assessmentId;
            return (
              <React.Fragment key={assessment.assessmentId}>
                {/* Main Assessment Row */}
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm">
                    <button
                      type="button"
                      onClick={() => toggleAssessmentExpand(assessment.assessmentId)}
                      className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                      title={isExpanded ? 'Collapse attempts' : 'Expand attempts'}
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-blue-600 font-medium">{assessment.assessment}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">{assessment.assessmentType}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{assessment.totalAttempts}</td>
                  <td className="px-6 py-3 text-sm font-semibold text-blue-600">
                    {parseFloat(assessment.bestScore).toFixed(2)}%
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">
                    {formatDate(assessment.lastAttemptDate)}
                  </td>
                </tr>

                {/* Expandable Attempts Rows */}
                {isExpanded && assessment.allAttempts && assessment.allAttempts.length > 0 && (
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <td colSpan="6" className="px-6 py-4">
                      <div className="ml-4">
                        <p className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                          All Attempts ({assessment.allAttempts.length})
                        </p>
                        <div className="space-y-2">
                          {assessment.allAttempts.map((attempt, index) => (
                            <div
                              key={attempt.attemptId}
                              className="flex items-center justify-between p-3 bg-white rounded border border-gray-200 text-sm"
                            >
                              <div className="flex items-center gap-3">
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                  Attempt {assessment.allAttempts.length - index}
                                </span>
                                <span className="text-gray-600">
                                  {formatDate(attempt.date)}
                                </span>
                              </div>
                              <span className="font-semibold text-blue-600">
                                {parseFloat(attempt.score).toFixed(2)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Server-side Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <div className="text-sm text-gray-600">
            Showing {startIndex} to {endIndex} of {totalCount} assessments
          </div>
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="p-1.5 text-gray-600 border border-gray-300 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-4 py-1.5 text-sm font-medium text-gray-700">
              {currentPage + 1} / {totalPages}
            </div>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              className="p-1.5 text-gray-600 border border-gray-300 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

AssessmentTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

// AssessmentTable.defaultProps = {
//   data: [],
//   currentPage: 0,
//   totalCount: 0,
// };